// src/modules/components/formspark-form.js

/**
 * Formspark Form Handler mit Botpoison Spam-Schutz
 * 
 * Verwendung in Webflow:
 * 1. Füge das Attribut data-module="formspark-form" zum Form-Wrapper hinzu
 * 2. Setze die Form action auf deine submit-form.com URL
 * 3. Benenne das Formular über data-formular="..." (siehe unten)
 * 4. Stelle sicher, dass .w-form-done und .w-form-fail Elemente vorhanden sind
 *
 * Beispiel:
 * <div data-module="formspark-form" data-formular="Freebie-Ratgeber">
 *   <form action="https://submit-form.com/YOUR_FORM_ID">
 *     <input type="text" name="name" required>
 *     <input type="email" name="email" required>
 *     <button type="submit">Senden</button>
 *   </form>
 *   <div class="w-form-done" style="display:none">Danke!</div>
 *   <div class="w-form-fail" style="display:none">Fehler!</div>
 * </div>
 */

// Konfiguration
const BOTPOISON_PUBLIC_KEY = "pk_2e06bdab-f5ff-457b-a9e4-f3e1be0d8d54";
const FORMSON_URL = "https://unpkg.com/@formspark/formson";
const BOTPOISON_URL = "https://unpkg.com/@botpoison/browser";

/**
 * Herkunft der Anfrage sichtbar machen.
 *
 * Formspark behandelt Felder, deren Name mit "_" beginnt, als Steuerbefehle
 * statt als Inhalt: _email.subject setzt den Betreff, _email.from den
 * Absendernamen, _email.template.title die Überschrift in der Mail und
 * _replyto die Antwortadresse. Felder ohne "_" landen zusätzlich als normale
 * Zeile in Mail und Dashboard.
 *
 * Doku: https://documentation.formspark.io/customization/notification-email.html
 */
const UMLAUT_ERSATZ = { "ä": "ae", "ö": "oe", "ü": "ue", "Ä": "Ae", "Ö": "Oe", "Ü": "Ue", "ß": "ss" };

/** Formspark erlaubt im Absendernamen nur Buchstaben, Zahlen, Leerzeichen, - und _ */
function alsAbsenderName(text) {
  return text
    .replace(/[äöüÄÖÜß]/g, (zeichen) => UMLAUT_ERSATZ[zeichen])
    .replace(/[^a-zA-Z0-9 \-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
}

const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Script Loader Helper
class ScriptLoader {
  static loadedScripts = new Set();
  
  static async load(url, globalName = null) {
    if (this.loadedScripts.has(url)) {
      return globalName ? window[globalName] : true;
    }
    
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      
      script.onload = () => {
        this.loadedScripts.add(url);
        resolve(globalName ? window[globalName] : true);
      };
      
      script.onerror = () => reject(new Error(`Failed to load script: ${url}`));
      
      document.head.appendChild(script);
    });
  }
}

// Form Handler Class
class FormsparkForm {
  constructor(form) {
    this.form = form;
    this.parent = form.closest("[data-module='formspark-form']") || form.parentElement;
    this.isSubmitting = false;
    this.successElement = this.parent.querySelector(".w-form-done");
    this.errorElement = this.parent.querySelector(".w-form-fail");
    this.action = form.getAttribute("action");
    this.redirectUrl = this.parent.dataset.redirect || null;
    this.formularName = this.ermittleFormularName();

    if (!this.action || !this.action.includes("submit-form.com")) {
      console.warn("FormsparkForm: Form action muss eine submit-form.com URL sein", form);
      return;
    }
    
    this.init();
  }
  
  /**
   * Name des Formulars. Bevorzugt data-formular am Wrapper, sonst aus der
   * Seite abgeleitet — damit auch ungepflegte Formulare nie namenlos ankommen.
   */
  ermittleFormularName() {
    const gesetzt = (this.parent.dataset.formular || "").trim();
    if (gesetzt) return gesetzt;

    const pfad = window.location.pathname.replace(/\/+$/, "");
    return `Anfrage über ${pfad || "/"}`;
  }

  /**
   * Legt die Steuer- und Herkunftsfelder als Hidden Inputs an.
   * In Webflow von Hand gesetzte Felder haben Vorrang und bleiben unangetastet.
   */
  setzeHerkunftsFelder() {
    const name = this.formularName;

    const felder = {
      "_email.subject": `${name} — ${window.location.hostname}`,
      "_email.template.title": name,
      "Formular": name,
      "Seite": window.location.href,
    };

    const absender = alsAbsenderName(name);
    if (absender) felder["_email.from"] = absender;

    Object.entries(felder).forEach(([feldName, wert]) => {
      if (this.form.querySelector(`[name="${CSS.escape(feldName)}"]`)) return;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = feldName;
      input.value = wert;
      this.form.appendChild(input);
    });
  }

  /**
   * Erste gültige E-Mail-Adresse im Formular. Felder mit "mail" im Namen
   * zuerst, damit ein Freitextfeld nicht versehentlich gewinnt.
   */
  findeAntwortAdresse(formData) {
    const eintraege = [...formData.entries()]
      .map(([schluessel, wert]) => [schluessel, String(wert).trim()])
      .filter(([, wert]) => EMAIL_MUSTER.test(wert));

    const ausMailFeld = eintraege.find(([schluessel]) => /mail/i.test(schluessel));
    return (ausMailFeld || eintraege[0] || [])[1] || null;
  }

  async init() {
    // Entferne Webflow's eigene Redirect-Logik
    this.form.removeAttribute("data-redirect");
    const redirectInput = this.form.querySelector('input[name="redirect"]');
    if (redirectInput) redirectInput.remove();

    // Herkunft der Anfrage in die Benachrichtigungsmail schreiben
    this.setzeHerkunftsFelder();

    // Event Listener
    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    
    // Lade externe Scripts
    try {
      await Promise.all([
        ScriptLoader.load(FORMSON_URL, "Formson"),
        ScriptLoader.load(BOTPOISON_URL, "Botpoison")
      ]);
      console.log("[FormsparkForm] ✓ Scripts geladen");
    } catch (error) {
      console.error("[FormsparkForm] Fehler beim Laden der Scripts:", error);
    }
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation(); // Webflow-Handler ausbremsen
    
    if (this.isSubmitting) return false;
    
    this.isSubmitting = true;
    this.hideMessages();
    
    try {
      // Botpoison Challenge
      const botpoison = new window.Botpoison({ 
        publicKey: BOTPOISON_PUBLIC_KEY 
      });
      
      const challenge = await botpoison.challenge();
      
      // Form Data sammeln
      const formData = new FormData(this.form);
      const data = window.Formson.toJSON(formData);
      data["_botpoison"] = challenge.solution;

      // Antwortadresse setzen, damit "Antworten" im Postfach direkt an die
      // anfragende Person geht. Formspark liest dafür _replyto, mail oder
      // email — unser Feld heißt "Email", deshalb setzen wir es explizit.
      if (!data["_replyto"]) {
        const antwortAdresse = this.findeAntwortAdresse(formData);
        if (antwortAdresse) data["_replyto"] = antwortAdresse;
      }

      // Submit
      const response = await fetch(this.action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        this.meldeFathomEvent(response);
        if (this.redirectUrl) {
          window.location.href = this.redirectUrl;
          return;
        }
        this.showSuccess();
        this.form.reset();
      } else {
        this.showError();
      }
      
    } catch (error) {
      console.error("[FormsparkForm] Submit Fehler:", error);
      this.showError();
    } finally {
      this.isSubmitting = false;
    }
    
    return false;
  }
  
  /**
   * Zählt die Anfrage als Conversion in Fathom — erst nach der Antwort von
   * Formspark, nicht beim Klick auf "Absenden". Formspark antwortet auch bei
   * verworfenen Anfragen mit HTTP 200 und meldet das nur im Kopf
   * formspark-status. Ist der Kopf nicht lesbar (CORS), zählt response.ok.
   */
  meldeFathomEvent(response) {
    const status = response.headers?.get?.("formspark-status");
    if (status && status !== "ok") return;

    try {
      window.fathom?.trackEvent(`Formular gesendet: ${this.formularName}`);
    } catch (error) {
      console.warn("[FormsparkForm] Fathom-Event nicht gesendet:", error);
    }
  }

  hideMessages() {
    if (this.successElement) this.successElement.style.display = "none";
    if (this.errorElement) this.errorElement.style.display = "none";
  }
  
  showSuccess() {
    this.form.style.display = "none";
    if (this.successElement) {
      this.successElement.style.display = "block";
    }
  }
  
  showError() {
    if (this.errorElement) {
      this.errorElement.style.display = "block";
    }
  }
}

// Export Init-Funktion
export function initFormsparkForms() {
  const formContainers = document.querySelectorAll("[data-module='formspark-form']");
  
  formContainers.forEach(container => {
    if (container.dataset.scriptInitialized) return;
    container.dataset.scriptInitialized = "true";
    
    const form = container.querySelector("form[action*='submit-form.com']");
    if (!form) {
      console.warn("[FormsparkForm] Kein Form mit submit-form.com action gefunden in:", container);
      return;
    }
    
    new FormsparkForm(form);
  });
  
  console.log(`[FormsparkForm] ✓ ${formContainers.length} Formulare initialisiert`);
}
